import * as React from "react";
import { useMinimumWageQuery } from "../useMinimumWageQuery";
import { MinimalWagePageProps } from "../types";
import { useMinimumWageMutation } from "../useMinimumWageMutation";
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext";
import { MinimumWageCountryLayout } from "../components/MinimumWageCountryLayout";
import { FieldName, MinimumWageOptionsSection } from "../containers/MinimumWageOptionsSection";
import { CalculationStartDateSection } from "../containers/CalculationStartDateSection";
import { MinimumWagePolishOptionsSection } from "../containers/MinimumWagePolishOptionsSection";
import { DriverQualificationsType } from "@/app/constants/driverQualificationsType";
import { SwedenDriverWorkYearsPeriods } from "@/app/constants/configuration/swedenDriverWorkYearsPeriods";
import { SwedenMinimumWageExpandableItemSection } from "../containers/SwedenMinimumWageExpandableItemSection";
import { SwedenMinimumWageConfigurationModel } from "@/app/models/configuration/minimumWage/swedenMinimumWageConfigurationModel";

import { PreLoader } from "../components/PreLoader"
import { buildMinimumWageDiffRenderers } from '../minimumWageDiffUtils';

const DRIVER_WORK_YEARS_PERIODS = [
  SwedenDriverWorkYearsPeriods.All,
  SwedenDriverWorkYearsPeriods.None,
  SwedenDriverWorkYearsPeriods.Below2,
  SwedenDriverWorkYearsPeriods.Above2,
  SwedenDriverWorkYearsPeriods.Above4,
  SwedenDriverWorkYearsPeriods.Above6,
]

const qualificationTypes = [
  DriverQualificationsType.NotSet,
  DriverQualificationsType.VehiclesTo3Comma5,
  DriverQualificationsType.VehiclesFrom3Comma5To11,
  DriverQualificationsType.VehiclesFrom11To19,
  DriverQualificationsType.VehiclesAbove19,
  DriverQualificationsType.HighQualifiedTruckDriver,
  DriverQualificationsType.BusDriver,
  DriverQualificationsType.BusDriverAndConductor,
  DriverQualificationsType.TouristBusDriver,
  DriverQualificationsType.BigBusDriver,
]

const defaultFields: FieldName[] = [
    "printCalculatedHours",
    "calculateCabotageAndCrossTrade",
]

const allowedCurrenciesInner = [
    'SEK',
]

const allowedCurrencies = [
    'SEK',
]

const translateKey = 'swedenConfiguration'

const country = 'sweden'

export const SwedenMinimumWageSettingsPage = ({ match }: MinimalWagePageProps) => {
    const { companyId } = match.params
    const q = useMinimumWageQuery(companyId)
    const up = useMinimumWageMutation(companyId)
    const { t } = useLocalize()

    const [state, setState] = React.useState<SwedenMinimumWageConfigurationModel | null>(null)

    React.useEffect(() => {
        setState(null)
    }, [companyId])

    React.useEffect(() => {
        if (q.data) {
            const newState = structuredClone(q.data.swedenConfiguration)
            setState(newState)
        }
    }, [q.data])

    const diffRenderers = React.useMemo(() => buildMinimumWageDiffRenderers('swedenConfiguration', t), [t])
    const diffLabels = React.useMemo<Record<string, string>>(() => ({
        configuration: t('configuration.minimumWages.options'),
        expandableItems: t('configuration.minimumWages.minimalHourRateTitle'),
    }), [t])

    if (!state || !q.data) {
        return <PreLoader />
    }

    const isDirty = JSON.stringify(state) !== JSON.stringify(q.data.swedenConfiguration)

    return (
        <MinimumWageCountryLayout
            selectedCountryValue={country}
            isDirty={isDirty}
            handleSave={() => up.mutateAsync({ ...q.data, swedenConfiguration: state })}
            handleCancel={() => setState(q.data.swedenConfiguration)}
            dataTestId="sweden-minimum-wage-settings-page"
            isLoading={(q.isFetching || up.isPending)}
            diffBefore={q.data.swedenConfiguration as Record<string, unknown>}
            diffAfter={state as Record<string, unknown>}
            diffLabels={diffLabels}
            diffRenderers={diffRenderers}
        >
            <CalculationStartDateSection
                state={state}
                setState={setState}
            />

            <div className={!state.configuration.calculateAmountsForCountry ? 'tw-opacity-50 tw-pointer-events-none' : ''}>
                    <SwedenMinimumWageExpandableItemSection
                        allowedCurrencies={allowedCurrenciesInner}
                        qualificationTypes={qualificationTypes} 
                        driverWorkYears={DRIVER_WORK_YEARS_PERIODS}
                        state={state}
                        setState={setState}
                    />

                    <MinimumWageOptionsSection
                        className='tw-my-5'
                        loading={false}
                        fields={defaultFields}
                        translateKey={translateKey}
                        state={state}
                        accommodationAndAllowanceMaxDate={q.data.generalConfiguration.accommodationAndAllowanceMaxDate}
                        setState={setState}
                    />

                    <MinimumWagePolishOptionsSection 
                        className='tw-my-5'
                        loading={false}
                        state={state}
                        setState={setState}
                    />
            </div>

            <div style={{ minHeight: '90px' }} />
        </MinimumWageCountryLayout>
    )
}
